import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { DialogHeader, DialogFooter } from '@/components/ui/Dialog';
import useSyncTaxonomyDefault from '@/hooks/useSyncTaxonomyDefault';

interface SyncTaxonomyButtonProps {
  disabled: boolean;
  selectedVersion: string;
}

export default function SyncTaxonomyButton({
  disabled,
  selectedVersion,
}: SyncTaxonomyButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { syncTaxonomyDefault, isSyncing } = useSyncTaxonomyDefault();

  function openSyncModal() {
    setIsModalOpen(true);
  }

  async function handleSync() {
    try {
      setIsModalOpen(false);

      const response = await syncTaxonomyDefault({
        version: selectedVersion,
      });

      if (response.success === true) {
        toast.success(response.message);
      }
    } catch (error) {
      toast.error('Error synchronizing taxonomy');
    }
  }

  return (
    <div>
      <Button
        onClick={openSyncModal}
        variant="outline"
        className="flex items-center gap-2"
        disabled={disabled}
      >
        <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
        {isSyncing ? 'Synchronizing...' : 'Sync Version'}
      </Button>

      {isModalOpen && (
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Version Synchronization</DialogTitle>

              <DialogDescription>
                Are you sure you want to synchronize the version? This action
                might affect existing data.
              </DialogDescription>
            </DialogHeader>

            <DialogFooter className="flex items-center justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>

              <Button onClick={handleSync} className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Synchronize
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
